import { useEffect } from "react";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { ENV } from "hub/src/appConstants/env";
import { isIframeSSOPage } from "hub/src/utils/isIframeSSOPage";

const IFRAME_PARENT_RETURN_URL_STORAGE_KEY = "iframeParentReturnUrl";

const isHubRedirectUrl = (
  candidateUrl: string,
  configuredRedirectUrl?: string,
): boolean => {
  if (!configuredRedirectUrl || typeof configuredRedirectUrl !== "string") {
    return false;
  }

  const normalizedRedirectUrl = configuredRedirectUrl.endsWith("/")
    ? configuredRedirectUrl.slice(0, -1)
    : configuredRedirectUrl;
  const normalizedCandidateUrl = candidateUrl.endsWith("/")
    ? candidateUrl.slice(0, -1)
    : candidateUrl;

  return (
    normalizedCandidateUrl === normalizedRedirectUrl ||
    normalizedCandidateUrl.startsWith(`${normalizedRedirectUrl}#`) ||
    normalizedCandidateUrl.startsWith(`${normalizedRedirectUrl}?`)
  );
};

const unknownToNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && !isNaN(value)) {
    return value;
  }
  if (typeof value === "string" && !isNaN(parseInt(value))) {
    return parseInt(value);
  }
  return null;
};

export const useIFrameCheckSession = ({
  profileId,
  sessionIsLoading,
  redirectUrlAfterLogin,
}: {
  sessionIsLoading: boolean;
  profileId: number | null | undefined;
  redirectUrlAfterLogin?: string;
}) => {
  const router = useCustomRouter();
  const ssoPagePath = ENV.REACT_APP_IFRAME_SSO_PATH;
  const redirectUrl = ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL;

  const parentOrigin = redirectUrl?.match(/https:\/\/[^/]+/)?.[0];

  const getReturnUrlFromCurrentSearch = () => {
    const fromSearch = new URLSearchParams(window.location.search).get(
      "ReturnUrl",
    );

    if (typeof fromSearch === "string" && fromSearch.length > 0) {
      return decodeURIComponent(fromSearch);
    }

    return null;
  };

  const getStoredParentReturnUrl = () => {
    const storedValue = sessionStorage.getItem(
      IFRAME_PARENT_RETURN_URL_STORAGE_KEY,
    );

    if (typeof storedValue === "string" && storedValue.length > 0) {
      return storedValue;
    }

    return null;
  };

  const getRelayState = (): string => {
    const returnUrlFromState =
      router.state?.ReturnUrl ?? router.state?.returnUrl;
    const returnUrlFromSearch = getReturnUrlFromCurrentSearch();
    const storedReturnUrl = getStoredParentReturnUrl();

    console.log("[Pillar checkSession] getRelayState candidates:", {
      returnUrlFromState,
      returnUrlFromSearch,
      storedReturnUrl,
      configuredRedirectUrl: redirectUrl,
      routerState: router.state,
      windowSearch: window.location.search,
      sessionStorageValue: sessionStorage.getItem(
        IFRAME_PARENT_RETURN_URL_STORAGE_KEY,
      ),
    });

    const orderedCandidates = [
      returnUrlFromState,
      returnUrlFromSearch,
      storedReturnUrl,
    ].filter(
      (candidate): candidate is string =>
        typeof candidate === "string" && candidate.length > 0,
    );

    const preferredCandidate = orderedCandidates.find(
      (candidate) => !isHubRedirectUrl(candidate, redirectUrl),
    );

    console.log("[Pillar checkSession] getRelayState result:", {
      orderedCandidates,
      preferredCandidate,
      isEachHubUrl: orderedCandidates.map((c) => ({
        candidate: c,
        isHubUrl: isHubRedirectUrl(c, redirectUrl),
      })),
    });

    if (preferredCandidate) {
      return preferredCandidate;
    }

    if (orderedCandidates.length > 0) {
      return orderedCandidates[0];
    }

    let relayState = redirectUrl ?? "";

    // No explicit ReturnUrl was supplied by the parent. Fall back to the hub
    // route the user is actually headed to (e.g. a token's routePath like
    // "/form/52", which has already been pushed onto the router by the time
    // this SSO redirect fires) so HigherLogic returns the parent to that route
    // after SSO instead of the bare hub landing page — letting the token's
    // destination finish resolving once the parent session is established.
    const destinationPath = redirectUrlAfterLogin ?? router.pathname;
    if (
      destinationPath &&
      destinationPath !== "/" &&
      destinationPath !== ssoPagePath
    ) {
      relayState += "#" + destinationPath;
    }

    return relayState;
  };

  const handleMessage = (event: MessageEvent) => {
    if (
      !isIframeSSOPage ||
      !redirectUrl ||
      typeof redirectUrl !== "string" ||
      !ssoPagePath ||
      typeof ssoPagePath !== "string" ||
      parentOrigin === undefined ||
      !event.origin ||
      event.origin !== parentOrigin
    ) {
      return;
    }
    if (event.data && event.data.type === "RESPOND_PROFILE_ID") {
      const parentProfileId = unknownToNumberOrNull(event.data.profileId);

      const sessionProfileId = unknownToNumberOrNull(profileId);

      console.log(
        "Received RESPOND_PROFILE_ID message from parent, profile ID:",
        {
          parentProfileId,
          sessionProfileId,
        },
      );
      if (parentProfileId === null && sessionProfileId !== null) {
        //Parent is not logged in but Pillar Hub is
        //Push to SSO page which will trigger the SSO flow
        const RelayState = getRelayState();
        const params = {
          ...router.state,
          RelayState,
        };
        router.push(ssoPagePath, params);
        console.log(
          `Parent profile ID is null but session profile ID is ${sessionProfileId}, redirecting to SSO page:`,
          {
            ssoPagePath,
            params,
          },
        );
        return;
      }
      if (sessionProfileId !== parentProfileId) {
        //Profile IDs don't match, trigger logout in parent
        console.log(
          `Profile ID mismatch between parent and session (parent: ${parentProfileId}, session: ${sessionProfileId}), triggering logout in parent`,
        );
        window.parent.postMessage(
          {
            type: "TRIGGER_LOGOUT",
            source: "pillar",
          },
          parentOrigin,
        );
      }
    }
  };
  useEffect(() => {
    if (
      !isIframeSSOPage ||
      !redirectUrl ||
      typeof redirectUrl !== "string" ||
      !ssoPagePath ||
      typeof ssoPagePath !== "string" ||
      parentOrigin === undefined ||
      sessionIsLoading
    ) {
      return;
    }
    window.addEventListener("message", handleMessage);
    window.parent.postMessage(
      {
        type: "REQUEST_PROFILE_ID",
        source: "pillar",
      },
      parentOrigin,
    );

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [profileId, sessionIsLoading]);
};
